/**
 * CustomerActionCenter — the customer's personal to-do list at the top
 * of their dashboard. Answers "Do I need to do anything?" in one glance.
 *
 * V1 pulls entirely from the already-loaded `orders` list so there's no
 * extra network call. Buckets:
 *   1. Artwork awaiting your approval           — click → artwork-review
 *   2. Artwork that needs a revision            — click → artwork-review
 *   3. Missing master files (submitted orders)  — click → order files
 *   4. Quotes waiting for your approval         — click → order detail
 *   5. Invoices awaiting payment                — passed in from parent
 *
 * The empty state is intentional: when there's nothing pending we
 * render a small "you're all caught up" card so the customer still
 * feels acknowledged.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import { Card } from './ui/card';
import {
    CheckCircle2,
    Palette,
    AlertCircle,
    FileText,
    CreditCard,
    ChevronRight,
    ClipboardList,
    Disc3,
} from 'lucide-react';

const IN_PROGRESS_STATUSES = ['Submitted', 'In Production', 'in_production', 'Shipping'];

const CustomerActionCenter = ({ orders = [], unpaidInvoices = [] }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const artworkApproval = orders.filter(
        (o) => o.artwork_status === 'Awaiting_Approval' && o.artwork_review_status !== 'rejected',
    );
    const artworkRevision = orders.filter((o) => o.artwork_review_status === 'rejected');
    // Artwork required but not yet uploaded. Only surface this on
    // orders that are past the Draft stage so new carts don't nag the
    // customer. Reorders that reuse the catalogue's approved artwork
    // (``reusable_artwork`` + no artwork_status) need no action — same
    // "Ready" treatment the orders list already applies. Any state
    // other than empty / null (Awaiting_Approval, Approved, Rejected)
    // belongs in a different bucket, so we only match the "truly
    // missing" case.
    const artworkRequired = orders.filter((o) => {
        if (!IN_PROGRESS_STATUSES.includes(o.status)) return false;
        if (o.artwork_status) return false;
        if (o.order_type === 'reorder') return false;
        if (o.reusable_artwork) return false;
        return true;
    });
    // Master file missing — mirrors the "Missing" badge in the Orders
    // list (DashboardPage master cell) so the Action Center never
    // disagrees with the row the customer is looking at. Signals used:
    //   * ``needs_master_revision`` — a previously uploaded master was
    //     rejected by production; customer must upload a replacement.
    //   * ``has_audio_master``       — a master file is attached.
    //   * ``reusable_master``        — reorder path reusing the
    //                                  catalogue's approved master.
    // Previously this bucket read ``master_status === 'Missing'``, a
    // field the backend never populates on the orders list, so #000209
    // etc. silently fell through — fixed by reusing the authoritative
    // flags above.
    const masterRequired = orders.filter((o) => {
        if (!IN_PROGRESS_STATUSES.includes(o.status)) return false;
        if (o.has_audio_master) return false;
        if (o.reusable_master) return false;
        // ``needs_master_revision`` falls through intentionally — the
        // customer still has an upload action to perform.
        return true;
    });
    // "Quotes waiting for your approval" must track the ACTUAL
    // quotation lifecycle, not just the main order status. An order
    // is actionable here when:
    //   1. The admin has sent at least one quotation version
    //      (status flips to "Quoted" at that point — "Quote" alone
    //      means the request is still awaiting admin pricing).
    //   2. The customer-visible snapshot has priced lines (total > 0)
    //      so the customer actually has something to approve.
    //   3. The customer has not yet approved or rejected that
    //      version (snapshot.status === 'sent'; approved / rejected
    //      snapshots don't belong here anymore).
    // Mirrors the same approvability gate used by <QuoteSection>'s
    // Approve button so the count can never overshoot what the
    // detail page lets the customer act on.
    const pendingQuotes = orders.filter((o) => {
        if (o.status !== 'Quoted') return false;
        const versions = Array.isArray(o.quote_versions) ? o.quote_versions : [];
        if (versions.length === 0) return false;
        const current = versions.find((v) => v.version === o.quote_current_version)
            || versions[versions.length - 1];
        if (!current) return false;
        // Snapshot must still be "sent" — approved / rejected / draft
        // / expired snapshots are not actionable from this bucket.
        if (current.status && current.status !== 'sent') return false;
        const total = Number(current.total);
        const lines = Array.isArray(current.lines) ? current.lines : [];
        return lines.length > 0 && Number.isFinite(total) && total > 0;
    });
    // Test Press ready for the customer's decision. "arrived" is the
    // state the state machine lands in the moment FLOW sends the TP
    // delivery notice (see routes/supplier.py). "approved"/"rejected"
    // are both terminal — those orders automatically drop out of the
    // Action Center with no extra logic.
    const testPressReview = orders.filter((o) => o.test_press_status === 'arrived');

    const orderNumberSubtitle = (o) => t('action.order_number', 'Order #{n}', { n: String(o.order_number).padStart(6, '0') });

    const items = [
        {
            key: 'artwork-approval',
            visible: artworkApproval.length > 0,
            icon: Palette,
            iconBg: 'bg-purple-50',
            iconColor: 'text-purple-600',
            title: artworkApproval.length === 1
                ? t('action.artwork_approval_one', '1 artwork ready for your approval')
                : t('action.artwork_approval_many', '{n} artworks ready for your approval', { n: artworkApproval.length }),
            subtitle: artworkApproval.length === 1
                ? orderNumberSubtitle(artworkApproval[0])
                : t('action.orders_waiting', '{n} orders waiting', { n: artworkApproval.length }),
            onClick: () => navigate(
                artworkApproval.length === 1
                    ? `/orders/${artworkApproval[0].id}/artwork-review`
                    : '/dashboard?filter=artwork-approval',
            ),
            testId: 'action-artwork-approval',
        },
        {
            key: 'artwork-revision',
            visible: artworkRevision.length > 0,
            icon: AlertCircle,
            iconBg: 'bg-amber-50',
            iconColor: 'text-amber-600',
            title: artworkRevision.length === 1
                ? t('action.artwork_revision_one', '1 artwork needs a revision')
                : t('action.artwork_revision_many', '{n} artworks need revisions', { n: artworkRevision.length }),
            subtitle: artworkRevision.length === 1
                ? orderNumberSubtitle(artworkRevision[0])
                : t('action.reject_reasons', 'Reject reasons available inside each order'),
            onClick: () => navigate(
                artworkRevision.length === 1
                    ? `/orders/${artworkRevision[0].id}/artwork-review`
                    : '/dashboard?filter=artwork-revision',
            ),
            testId: 'action-artwork-revision',
        },
        {
            // Artwork required — order is Submitted (or further
            // along) and the customer still needs to upload. Deep-
            // link straight to the order files tab so the upload UI
            // is one tap away. Stays visible until artwork_status
            // moves to Awaiting_Approval / Approved — then another
            // bucket takes over.
            //
            // Copy pattern mirrors every blocking customer action:
            // state WHAT the customer needs to do and that it is
            // required TO CONTINUE THE ORDER. Order# + title still
            // ride below so the customer can confirm which order
            // they are being asked about.
            key: 'artwork-required',
            visible: artworkRequired.length > 0,
            icon: Palette,
            iconBg: 'bg-rose-50',
            iconColor: 'text-rose-600',
            title: t('action.artwork_required_title', 'Artwork required'),
            subtitle: artworkRequired.length === 1
                ? `${t('action.artwork_required_cta', 'Upload artwork to continue your order')} · ${orderNumberSubtitle(artworkRequired[0])}${artworkRequired[0].title ? ` · ${artworkRequired[0].title}` : ''}`
                : `${t('action.artwork_required_cta_many', 'Upload artwork to continue your orders')} · ${t('action.orders_waiting', '{n} orders waiting', { n: artworkRequired.length })}`,
            onClick: () => navigate(
                artworkRequired.length === 1
                    ? `/orders/${artworkRequired[0].id}/files#artwork`
                    : '/dashboard?filter=artwork-required',
            ),
            testId: 'action-artwork-required',
        },
        {
            // Master required — mirrors the Admin Orders list "Missing"
            // badge for the master cell. Deep-links to the order
            // files page where the Master upload component lives;
            // the ``#master`` hash gets picked up by the MasterFilesCard
            // auto-scroll on OrderFilesPage.
            key: 'master-required',
            visible: masterRequired.length > 0,
            icon: FileText,
            iconBg: 'bg-rose-50',
            iconColor: 'text-rose-600',
            title: t('action.master_required_title', 'Master required'),
            subtitle: masterRequired.length === 1
                ? `${t('action.master_required_cta', 'Upload your master to continue your order')} · ${orderNumberSubtitle(masterRequired[0])}${masterRequired[0].title ? ` · ${masterRequired[0].title}` : ''}`
                : `${t('action.master_required_cta_many', 'Upload your masters to continue your orders')} · ${t('action.orders_waiting', '{n} orders waiting', { n: masterRequired.length })}`,
            onClick: () => navigate(
                masterRequired.length === 1
                    ? `/orders/${masterRequired[0].id}/files#master`
                    : '/dashboard?filter=missing-master',
            ),
            testId: 'action-master-required',
        },
        {
            // Test Press ready for review. Deep-links to the Order
            // Details tab (customer workspace) where the TP card sits
            // at the top with Approve / Reject buttons. The URL hash
            // scrolls the card into view.
            key: 'test-press-review',
            visible: testPressReview.length > 0,
            icon: Disc3,
            iconBg: 'bg-amber-50',
            iconColor: 'text-amber-600',
            title: testPressReview.length === 1
                ? t('action.test_press_review_one', 'Test press ready for review')
                : t('action.test_press_review_many', '{n} test presses ready for review', { n: testPressReview.length }),
            subtitle: testPressReview.length === 1
                ? orderNumberSubtitle(testPressReview[0])
                : t('action.test_press_review_hint', 'Approve or reject each test press inside your order'),
            onClick: () => navigate(
                testPressReview.length === 1
                    ? `/orders/${testPressReview[0].id}/files#test-press`
                    : '/dashboard?filter=test-press-review',
            ),
            testId: 'action-test-press-review',
        },
        {
            key: 'pending-quotes',
            visible: pendingQuotes.length > 0,
            icon: ClipboardList,
            iconBg: 'bg-blue-50',
            iconColor: 'text-blue-600',
            title: pendingQuotes.length === 1
                ? t('action.quote_one', '1 quote waiting for your approval')
                : t('action.quote_many', '{n} quotes waiting for your approval', { n: pendingQuotes.length }),
            subtitle: pendingQuotes.length === 1
                ? orderNumberSubtitle(pendingQuotes[0])
                : t('action.quote_hint', 'Approve or reject inside each order'),
            onClick: () => navigate(
                pendingQuotes.length === 1
                    ? `/orders/${pendingQuotes[0].id}`
                    : '/dashboard?filter=quote',
            ),
            testId: 'action-pending-quotes',
        },
        {
            key: 'unpaid-invoices',
            visible: unpaidInvoices.length > 0,
            icon: CreditCard,
            iconBg: 'bg-indigo-50',
            iconColor: 'text-indigo-600',
            title: unpaidInvoices.length === 1
                ? t('action.invoice_one', '1 invoice awaiting payment')
                : t('action.invoice_many', '{n} invoices awaiting payment', { n: unpaidInvoices.length }),
            subtitle: t('action.invoice_hint', 'Open Invoices to see amounts and due dates'),
            onClick: () => navigate('/invoices'),
            testId: 'action-unpaid-invoices',
        },
    ].filter((i) => i.visible);

    return (
        <Card className="mb-4" data-testid="customer-action-center">
            <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-slate-100 rounded-md">
                        <ClipboardList className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-slate-900 leading-tight">{t('action.title', 'Your Action Center')}</h2>
                        <p className="text-[11px] text-slate-500 leading-tight">{t('action.subtitle', 'Do I need to do anything?')}</p>
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="px-4 py-4 flex items-center gap-3 text-sm text-slate-600" data-testid="customer-action-center-empty">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <div>
                        <p className="font-medium text-slate-900">{t('action.caught_up_title', "You're all caught up.")}</p>
                        <p className="text-[11px] text-slate-500">
                            {t('action.caught_up_desc', "We'll flag any orders here when they need your input.")}
                        </p>
                    </div>
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map((it) => {
                        const Icon = it.icon;
                        return (
                            <li key={it.key}>
                                <button
                                    type="button"
                                    onClick={it.onClick}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                                    data-testid={it.testId}
                                >
                                    <div className={`p-1.5 rounded-md ${it.iconBg} flex-shrink-0`}>
                                        <Icon className={`h-4 w-4 ${it.iconColor}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 leading-tight truncate">{it.title}</p>
                                        <p className="text-[11px] text-slate-500 leading-tight truncate">{it.subtitle}</p>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
};

export default CustomerActionCenter;
